import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import { messageFromError } from "../../../shared/api/drfErrors";
import Select from "../../../shared/ui/Field/Select";
import {
  fetchCompany,
  fetchMembers,
  fetchMyCompany,
} from "../../companyWorkspace/api/companyWorkspace";
import {
  isActiveMembership,
  isAdminMembership,
} from "../../companyWorkspace/lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../../companyWorkspace/lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  useCompanyFocusRevalidation,
} from "../../companyWorkspace/lib/companyFocusRevalidation";
import CompanyPageHeader from "../../companyWorkspace/ui/CompanyPageHeader";
import CompanyWorkViewSwitch from "./CompanyWorkViewSwitch";
import {
  buildCalendarGrid,
  WEEKDAY_HEADERS,
} from "../../tasksCalendar/lib/calendarGrid";
import {
  currentLocalMonth,
  localDateString,
  parseDayParam,
  parseMonthParam,
  resolveAutoSelectedDay,
} from "../../tasksCalendar/lib/calendarCodec";
import {
  getLocalMonthLabel,
  nextMonth,
  prevMonth,
} from "../../tasksCalendar/lib/calendarMonth";
import { formatLocalDate } from "../../tasksCalendar/lib/calendarGrouping";
import { fetchAllCompanyWorkPages } from "../api/companyWorkTransport";
import {
  companyMonthRangeParams,
  groupCalendarItemsByDay,
  toCalendarPresentation,
} from "../lib/workCalendar";
import {
  formatDateTime,
  memberDisplayName,
  membershipGenerationOf,
  taskStatusLabel,
  viewingStatusLabel,
} from "../lib/workLabels";
import {
  TASK_STATUSES,
  VIEWING_STATUSES,
} from "../lib/workQuery";

function unwrapMembers(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function readCalendarFilters(searchParams) {
  const status = searchParams.get("status") || "";
  const responsibleRaw = searchParams.get("responsible_user") || "";
  const responsible_user =
    responsibleRaw === "unassigned" || /^\d+$/.test(responsibleRaw)
      ? responsibleRaw
      : "";
  const client = searchParams.get("client") || "";
  const property = searchParams.get("property") || "";
  return { status, responsible_user, client, property };
}

function CompanyWorkCalendarContent() {
  const { companyId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const abortRef = useRef(null);

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const month = parseMonthParam(searchParams.get("month"));
  const day = resolveAutoSelectedDay(
    month,
    parseDayParam(searchParams.get("day"), month),
  );
  const filters = useMemo(
    () => readCalendarFilters(searchParams),
    [searchParams],
  );

  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [members, setMembers] = useState([]);
  const [items, setItems] = useState([]);
  const [undatedTasks, setUndatedTasks] = useState([]);
  const [state, setState] = useState("loading");
  const stateRef = useRef(state);
  stateRef.current = state;
  const [error, setError] = useState(null);
  const [focusNote, setFocusNote] = useState(null);
  const [complete, setComplete] = useState(false);

  const companyMatches =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const authorityMatches = companyMatches && actorMatches;
  const isOa = isAdminMembership(company, membership);

  const replaceCalendarSearch = useCallback(
    (patch) => {
      const params = new URLSearchParams(location.search);
      Object.entries(patch).forEach(([key, value]) => {
        if (value == null || value === "") params.delete(key);
        else params.set(key, String(value));
      });
      navigate(
        {
          pathname: `/companies/${companyId}/tasks/calendar`,
          search: `?${params.toString()}`,
        },
        { replace: true },
      );
    },
    [companyId, location.search, navigate],
  );

  const setMonthDay = useCallback(
    (nextMonthValue, nextDay) => {
      replaceCalendarSearch({
        month: nextMonthValue,
        day: nextDay || "",
      });
    },
    [replaceCalendarSearch],
  );

  const clearProtected = useCallback(() => {
    setItems([]);
    setUndatedTasks([]);
    setComplete(false);
    setError(null);
    setMembers([]);
  }, []);

  const load = useCallback(async ({ background = false } = {}) => {
    if (!companyId) {
      setState("not-found");
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const keepVisible = background && stateRef.current === "ready";
    if (!keepVisible) {
      setState("loading");
      setError(null);
      setComplete(false);
      setItems([]);
      setUndatedTasks([]);
    }
    let op = scopeGate.beginRead({
      companyId,
      actorId,
      resourceId: `calendar:${month}`,
    });
    try {
      const [companyData, mine] = await Promise.all([
        fetchCompany(companyId),
        fetchMyCompany(),
      ]);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const mineMatches =
        mine?.company?.id != null && sameCompanyId(mine.company.id, companyId)
          ? mine.membership
          : null;
      const nextGeneration = membershipGenerationOf(mineMatches);
      setCompany(companyData);
      setMembership(mineMatches);
      setAuthorityActorId(actorId);

      scopeGate.bindIdentity({
        companyId,
        actorId,
        membershipGeneration: nextGeneration,
        resourceId: `calendar:${month}`,
        bindMembershipGeneration: true,
        bindResourceId: true,
      });

      if (!isActiveMembership(companyData, mineMatches)) {
        clearProtected();
        setState("denied");
        return;
      }

      op = scopeGate.beginRead({
        companyId,
        actorId,
        membershipGeneration: nextGeneration,
        resourceId: `calendar:${month}`,
      });

      if (isAdminMembership(companyData, mineMatches)) {
        try {
          const memberData = await fetchMembers(companyId, "active");
          if (scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
            setMembers(unwrapMembers(memberData));
          }
        } catch {
          /* ignore */
        }
      }

      const sharedFilters = {};
      if (filters.status) sharedFilters.status = filters.status;
      if (filters.responsible_user) {
        sharedFilters.responsible_user = filters.responsible_user;
      }
      if (filters.client) sharedFilters.client = filters.client;
      if (filters.property) sharedFilters.property = filters.property;

      const taskParams = {
        ...companyMonthRangeParams(month, "task"),
        ...sharedFilters,
      };
      const viewingParams = {
        ...companyMonthRangeParams(month, "viewing"),
        ...sharedFilters,
      };
      // Status is singular and type-specific — drop incompatible values.
      if (
        taskParams.status &&
        !TASK_STATUSES.includes(taskParams.status)
      ) {
        delete taskParams.status;
      }
      if (
        viewingParams.status &&
        !VIEWING_STATUSES.includes(viewingParams.status)
      ) {
        delete viewingParams.status;
      }

      const [tasksResult, viewingsResult] = await Promise.all([
        fetchAllCompanyWorkPages(companyId, "task", taskParams, {
          signal: controller.signal,
        }),
        fetchAllCompanyWorkPages(companyId, "viewing", viewingParams, {
          signal: controller.signal,
        }),
      ]);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      if (tasksResult.aborted || viewingsResult.aborted) return;
      if (!tasksResult.ok || !viewingsResult.ok) {
        setComplete(false);
        setError(
          tasksResult.error ||
            viewingsResult.error ||
            "Неповне завантаження календаря",
        );
        setState("error");
        return;
      }

      const presented = [];
      const noDeadline = [];
      for (const row of tasksResult.rows) {
        const item = toCalendarPresentation(row, "task");
        if (!item.day) {
          noDeadline.push(item);
        } else {
          presented.push(item);
        }
      }
      for (const row of viewingsResult.rows) {
        presented.push(toCalendarPresentation(row, "viewing"));
      }
      setItems(presented);
      setUndatedTasks(noDeadline);
      setComplete(true);
      setFocusNote(null);
      setState("ready");
    } catch (err) {
      if (
        op &&
        !scopeGate.readStillValid(op, { mounted: mountedRef.current })
      ) {
        return;
      }
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      const status = err?.response?.status;
      if (keepVisible && status !== 403 && status !== 404) {
        setFocusNote(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
        return;
      }
      setComplete(false);
      setError(messageFromError(err));
      setState("error");
    }
  }, [
    actorId,
    clearProtected,
    companyId,
    filters.client,
    filters.property,
    filters.responsible_user,
    filters.status,
    month,
    scopeGate,
  ]);

  useEffect(() => {
    mountedRef.current = true;
    // Bind current calendar month resource BEFORE authority discovery so a
    // prior calendar:YYYY-MM cannot reject the new beginRead via stale resourceId.
    scopeGate.bindIdentity({
      companyId,
      actorId,
      resourceId: `calendar:${month}`,
      bindResourceId: true,
    });
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      if (abortRef.current) abortRef.current.abort();
    };
  }, [companyId, actorId, month, filters.status, filters.responsible_user, filters.client, filters.property]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: state === "ready",
    busy: false,
    onRevalidate: () => load({ background: true }),
  });

  const byDay = useMemo(() => groupCalendarItemsByDay(items), [items]);
  const cells = useMemo(() => buildCalendarGrid(month), [month]);
  const agenda = day && complete ? byDay.get(day) || [] : [];
  const today = localDateString(new Date());
  const listSearch = (() => {
    const params = new URLSearchParams();
    if (filters.status) params.set("status", filters.status);
    if (filters.responsible_user) {
      params.set("responsible_user", filters.responsible_user);
    }
    if (filters.client) params.set("client", filters.client);
    if (filters.property) params.set("property", filters.property);
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  })();

  const statusLabelFor = (item) =>
    item.kind === "viewing"
      ? viewingStatusLabel(item.status)
      : taskStatusLabel(item.status);

  const agendaTime = (item) => {
    const raw =
      item.kind === "viewing"
        ? item.raw?.scheduled_at
        : item.raw?.due_date;
    return raw ? formatDateTime(raw) : "";
  };

  return (
    <section
      className="clients-page feature-company-workspace feature-tasks-calendar feature-company-work"
      id="Csection"
      tabIndex="-1"
    >
      <div className="container">
      <CompanyPageHeader
        title="Календар компанії"
        subtitle="Задачі та перегляди компанії"
        company={authorityMatches ? company : null}
        membership={authorityMatches ? membership : null}
        companyId={companyId}
        activeNav="tasks"
      />

      <CompanyWorkViewSwitch
        activeView="calendar"
        listHref={`/companies/${companyId}/tasks${listSearch}`}
        calendarHref={`/companies/${companyId}/tasks/calendar${location.search}`}
      />

      <div className="feature-tasks-calendar__toolbar">
        <div className="feature-tasks-calendar__toolbar-nav">
          <button
            type="button"
            className="action-btn feature-tasks-calendar__nav-btn"
            aria-label="Попередній місяць ←"
            onClick={() => setMonthDay(prevMonth(month), null)}
          >
            ←
          </button>
          <h2 className="feature-tasks-calendar__month-heading">
            {getLocalMonthLabel(month)}
          </h2>
          <button
            type="button"
            className="action-btn feature-tasks-calendar__nav-btn"
            aria-label="Наступний місяць →"
            onClick={() => setMonthDay(nextMonth(month), null)}
          >
            →
          </button>
          <button
            type="button"
            className="action-btn feature-tasks-calendar__today-btn"
            onClick={() => {
              const now = currentLocalMonth();
              setMonthDay(now, resolveAutoSelectedDay(now, null));
            }}
          >
            Сьогодні
          </button>
        </div>
        <div className="feature-tasks-calendar__filter-chips">
          <label className="ui-form__field">
            <span>Статус</span>
            <Select
              value={filters.status}
              onChange={(e) =>
                replaceCalendarSearch({ status: e.target.value })
              }
              options={[
                { value: "", label: "Усі" },
                ...[...new Set([...TASK_STATUSES, ...VIEWING_STATUSES])].map(
                  (s) => ({
                    value: s,
                    label: VIEWING_STATUSES.includes(s)
                      ? viewingStatusLabel(s)
                      : taskStatusLabel(s),
                  }),
                ),
              ]}
            />
          </label>
          {isOa ? (
            <label className="ui-form__field">
              <span>Виконавець</span>
              <Select
                value={filters.responsible_user}
                onChange={(e) =>
                  replaceCalendarSearch({
                    responsible_user: e.target.value,
                  })
                }
                options={[
                  { value: "", label: "Усі" },
                  { value: "unassigned", label: "Непризначені" },
                  ...(actorId != null
                    ? [{ value: String(actorId), label: "Мої" }]
                    : []),
                  ...members
                    .map((m) => {
                      const userId = m.user?.id ?? m.user_id;
                      if (userId == null) return null;
                      if (actorId != null && String(userId) === String(actorId)) {
                        return null;
                      }
                      return {
                        value: String(userId),
                        label: memberDisplayName(m) || `Учасник #${userId}`,
                      };
                    })
                    .filter(Boolean),
                ]}
              />
            </label>
          ) : null}
        </div>
      </div>

      {state === "loading" ? (
        <Spinner label="Завантаження календаря" />
      ) : null}
      {state === "error" ? (
        <EmptyState
          title="Календар неповний"
          description={
            error ||
            "Не вдалося завантажити всі сторінки. Часткові дані не показуються."
          }
          primaryAction={
            <button type="button" className="action-btn" onClick={load}>
              Повторити
            </button>
          }
        />
      ) : null}
      {state === "denied" ? (
        <EmptyState title="Немає доступу" description="Потрібне активне членство." />
      ) : null}

      {state === "ready" && focusNote ? (
        <p className="feature-company-workspace__error" role="alert">
          {focusNote}
        </p>
      ) : null}

      {state === "ready" && complete ? (
        <>
          <div className="feature-tasks-calendar__grid" role="grid">
            <div className="feature-tasks-calendar__weekdays">
              {WEEKDAY_HEADERS.map((h) => (
                <div key={h}>{h}</div>
              ))}
            </div>
            <div className="feature-tasks-calendar__cells">
              {cells.map((cell, index) => {
                if (!cell.dateStr) {
                  return (
                    <div
                      key={`empty-${index}`}
                      className="feature-tasks-calendar__cell feature-tasks-calendar__cell--muted feature-tasks-calendar__cell--adjacent"
                    >
                      <span className="feature-tasks-calendar__cell-day" />
                    </div>
                  );
                }
                const dayItems = byDay.get(cell.dateStr) || [];
                const selected = day === cell.dateStr;
                const isToday = cell.dateStr === today;
                return (
                  <div
                    key={cell.dateStr}
                    className={[
                      "feature-tasks-calendar__cell",
                      selected
                        ? "feature-tasks-calendar__cell--selected"
                        : "",
                      isToday ? "feature-tasks-calendar__cell--today" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <button
                      type="button"
                      className="feature-tasks-calendar__day-select"
                      onClick={() => setMonthDay(month, cell.dateStr)}
                    >
                      <span className="feature-tasks-calendar__cell-day">
                        {cell.dateStr.slice(-2)}
                      </span>
                      {dayItems.length > 0 ? (
                        <span
                          className="feature-tasks-calendar__cell-count"
                          aria-hidden="true"
                        >
                          {dayItems.length}
                        </span>
                      ) : null}
                      <span className="feature-tasks-calendar__cell-chips">
                        {dayItems.slice(0, 3).map((item) => (
                          <span
                            key={item.key}
                            className={[
                              "feature-tasks-calendar__chip",
                              item.kind === "viewing"
                                ? "feature-company-work__pill--viewing"
                                : "feature-company-work__pill--task",
                              item.overdue
                                ? "feature-tasks-calendar__chip--overdue"
                                : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          >
                            <span className="feature-tasks-calendar__chip-title">
                              {item.title}
                            </span>
                          </span>
                        ))}
                        {dayItems.length > 3 ? (
                          <span className="feature-tasks-calendar__chip-overflow">
                            +{dayItems.length - 3}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <section
            className="feature-tasks-calendar__agenda"
            aria-label="Порядок дня"
          >
            <h3>{day ? formatLocalDate(day) : "Оберіть день"}</h3>
            {!day ? (
              <p className="feature-company-workspace__meta">
                Оберіть день у сітці.
              </p>
            ) : agenda.length === 0 ? (
              <EmptyState title="Порожній день" description="Немає подій." />
            ) : (
              <ul>
                {agenda.map((item) => (
                  <li
                    key={item.key}
                    className={[
                      "feature-tasks-calendar__agenda-row",
                      item.overdue
                        ? "feature-tasks-calendar__agenda-row--overdue"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <div className="feature-tasks-calendar__agenda-row-main">
                      <Link
                        to={
                          item.kind === "viewing"
                            ? `/companies/${companyId}/viewings/${item.id}`
                            : `/companies/${companyId}/tasks/${item.id}`
                        }
                      >
                        {item.title}
                        {agendaTime(item) ? ` · ${agendaTime(item)}` : ""}
                        {" · "}
                        {statusLabelFor(item)}
                        {item.overdue ? " · Прострочено" : ""}
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {undatedTasks.length > 0 ? (
            <section aria-label="Задачі без дедлайну">
              <h3>Задачі без дедлайну (не на календарі)</h3>
              <ul>
                {undatedTasks.map((item) => (
                  <li key={item.key}>
                    <Link to={`/companies/${companyId}/tasks/${item.id}`}>
                      {item.title} · {taskStatusLabel(item.status)}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      ) : null}
      </div>
    </section>
  );
}

export default CompanyWorkCalendarContent;
